import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from "../ui/dropdown-menu";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from "../ui/table";
import { RuleFormData } from "./builderSchema";
import { Code, Type } from "../../shared/schema";
import { useAttributes, useRulesFunctionData } from "@/hooks/useLocalStorage";

interface ParameterSelection {
    dataType: string;
    attribute: string;
}

interface ExpressionRow {
    id: number;
    selected: boolean;
    dataType1: string;
    attribute1: string;
    FunctionName: string;
    Description: string;
    ParamCount: number;
    parameters: ParameterSelection[];
}

interface RuleExpressionSectionProps {
    showExpressionBuilder: boolean;
    setShowExpressionBuilder: (show: boolean) => void;
    dataType: Type[];
}

export default function RuleExpressionSection({
    showExpressionBuilder,
    setShowExpressionBuilder,
    dataType
}: RuleExpressionSectionProps) {
    const { watch, setValue } = useFormContext<RuleFormData>();
    const ruleExpression = watch("ruleExpression");
    const RuleExpressionJson = watch("ruleExpressionJson");
    const [expressionRows, setExpressionRows] = useState<ExpressionRow[]>([]);
    const [attrCode, setAttrCode] = useState<string>("");
    const [attribute, setAttribute] = useState<Code[]>([]);
    const [addWarning, setAddWarning] = useState<string>("")

    const { data: types, isLoading } = useRulesFunctionData(1, undefined, undefined);
    const { data: existingAttribute } = useAttributes(attrCode);

    useEffect(() => {
        if (existingAttribute && existingAttribute.length > 0) {
            setAttribute(existingAttribute);
        } else {
            setAttribute([]);
        }
    }, [existingAttribute]);

    useEffect(() => {
        if (types?.items) {
            const filteredItems = types.items.filter((item: any) => item.Configured === true);
            let parsedExpressionJson = RuleExpressionJson;
            if (typeof RuleExpressionJson === 'string' && RuleExpressionJson.trim()) {
                try {
                    parsedExpressionJson = JSON.parse(RuleExpressionJson);
                } catch (error) {
                    console.error("Error parsing RuleExpressionJson:", error);
                    parsedExpressionJson = "";
                }
            }

            if (parsedExpressionJson && Array.isArray(parsedExpressionJson) && parsedExpressionJson.length > 0) {
                const rows: ExpressionRow[] = filteredItems.map((item: any) => {
                    const existingExpr = parsedExpressionJson.find((expr: any) => expr.id === item.Id);
                    if (existingExpr) {
                        const [dataType1 = "", attribute1 = ""] = existingExpr.DataType?.split(".") || ["", ""];

                        const parameters = (existingExpr.Parameters || []).map((param: string) => {
                            const [dataType = "", attribute = ""] = param.split(".");
                            return { dataType, attribute };
                        });

                        while (parameters.length < (item.ParamCount || 0)) {
                            parameters.push({ dataType: "", attribute: "" });
                        }

                        return {
                            id: item.Id,
                            selected: true,
                            dataType1,
                            attribute1,
                            FunctionName: item.FunctionName,
                            Description: item.Description,
                            ParamCount: item.ParamCount || 0,
                            parameters
                        };
                    }

                    return {
                        id: item.Id,
                        selected: false,
                        dataType1: "",
                        attribute1: "",
                        FunctionName: item.FunctionName,
                        Description: item.Description,
                        ParamCount: item.ParamCount || 0,
                        parameters: Array.from({ length: item.ParamCount || 0 }, () => ({
                            dataType: "",
                            attribute: ""
                        }))
                    };
                });
                setExpressionRows(rows);
            } else {
                const rows: ExpressionRow[] = filteredItems.map((item: any) => ({
                    id: item.Id,
                    selected: false,
                    dataType1: "",
                    attribute1: "",
                    FunctionName: item.FunctionName,
                    Description: item.Description,
                    ParamCount: item.ParamCount || 0,
                    parameters: Array.from({ length: item.ParamCount || 0 }, () => ({
                        dataType: "",
                        attribute: ""
                    }))
                }));
                setExpressionRows(rows);
            }
        }
    }, [types, RuleExpressionJson]);

    const handleDataTypeChange = (rowId: number, dataTypeId: string, dataTypeName: string, isMainDataType: boolean, paramIndex?: number) => {
        setExpressionRows(prev =>
            prev.map(r => {
                if (r.id === rowId) {
                    if (isMainDataType) {
                        return { ...r, dataType1: dataTypeName, attribute1: "" };
                    } else if (paramIndex !== undefined) {
                        const newParams = [...r.parameters];
                        newParams[paramIndex] = { ...newParams[paramIndex], dataType: dataTypeName, attribute: "" };
                        return { ...r, parameters: newParams };
                    }
                }
                return r;
            })
        );

        if (dataTypeId) {
            setAttrCode(dataTypeId);
        }
    };

    const handleAttributeChange = (rowId: number, attribute: string, isMainDataType: boolean, paramIndex?: number) => {
        setExpressionRows(prev =>
            prev.map(r => {
                if (r.id === rowId) {
                    if (isMainDataType) {
                        return { ...r, attribute1: attribute };
                    } else if (paramIndex !== undefined) {
                        const newParams = [...r.parameters];
                        newParams[paramIndex] = { ...newParams[paramIndex], attribute };
                        return { ...r, parameters: newParams };
                    }
                }
                return r;
            })
        );
    };

    const handleSaveExpression = () => {
        const selectedRows = expressionRows.filter(r => r.selected);

        if (selectedRows.length === 0) {
            setAddWarning("Please select at least one expression");
            setTimeout(() => setAddWarning(""), 2000);
            return;
        }

        const expressions: string[] = [];
        const jsonExpressions: any[] = [];

        for (const row of selectedRows) {

            if (!row.dataType1?.trim() || !row.attribute1?.trim()) {
                setAddWarning("Please fill all DataType and Attribute fields");
                setTimeout(() => setAddWarning(""), 2000);
                return;
            }

            const allParamsFilled = row.parameters.every(p =>
                typeof p.dataType === "string" &&
                typeof p.attribute === "string" &&
                p.dataType.trim() !== "" &&
                p.attribute.trim() !== ""
            );

            if (!allParamsFilled) {
                setAddWarning("Please fill all parameter fields");
                setTimeout(() => setAddWarning(""), 2000);
                return;
            }

            const paramString = row.parameters
                .map(p => `${p.dataType}.${p.attribute}`)
                .join(",");

            const expression = `${row.dataType1}.${row.attribute1}=RuleFunctionLibrary.${row.FunctionName.replace(/\s+/g, '')}(${paramString})`;

            expressions.push(expression);

            jsonExpressions.push({
                id: row.id,
                DataType: `${row.dataType1}.${row.attribute1}`,
                FunctionName: row.FunctionName,
                Parameters: row.parameters.map(p => `${p.dataType}.${p.attribute}`)
            });
        }

        setValue("ruleExpression", expressions.join("\n"));
        setValue("ruleExpressionJson", JSON.stringify(jsonExpressions));
        setShowExpressionBuilder(false);
    };


    return (
        <div className="border bg-white border-gray-200 rounded-lg p-2">
            <div className="flex flex-col gap-2">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowExpressionBuilder(!showExpressionBuilder)}
                    className="flex items-center gap-2 w-fit"
                >
                    {showExpressionBuilder ? "Close Rule Expression" : "Set Rule Expression"}
                </Button>

                {showExpressionBuilder && (
                    <div className="border rounded-lg p-4 mt-4 bg-white">
                        <h3 className="text-lg font-semibold mb-3">Set Rule Expression</h3>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-gray-100">
                                        <TableHead className="border p-2 text-center">Select</TableHead>
                                        <TableHead className="border p-2">Data Type & Attribute</TableHead>
                                        <TableHead className="border p-2 text-center">Operator</TableHead>
                                        <TableHead className="border p-2">Function</TableHead>
                                        <TableHead className="border p-2">Parameters</TableHead>
                                        <TableHead className="border p-2">Description</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {expressionRows.map((row) => (
                                        <TableRow key={row.id}>
                                            <TableCell className="border p-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={row.selected}
                                                    onChange={(e) =>
                                                        setExpressionRows(prev =>
                                                            prev.map(r =>
                                                                r.id === row.id ? { ...r, selected: e.target.checked } : r
                                                            )
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell className="border p-2">
                                                <div className="flex gap-2">
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-between text-sm">
                                                                {dataType.length > 0 ? !row.dataType1 ? "Select" : row.dataType1 : "Fetching DataType..."}
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            {dataType.map((dt) => (
                                                                <DropdownMenuItem
                                                                    className="w-[280px]"
                                                                    key={dt.ID}
                                                                    onClick={() => handleDataTypeChange(row.id, dt.ID?.toString() || "", dt.Name, true)}
                                                                >
                                                                    {dt.Name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>

                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button variant="outline" className="w-full justify-between text-sm" disabled={!row.dataType1 || attribute.length === 0}>
                                                                {row.dataType1 ? !row.attribute1 ? "Select" : row.attribute1 : "Select"}
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent>
                                                            {attribute.map((opt) => (
                                                                <DropdownMenuItem
                                                                    className="w-[250px]"
                                                                    key={opt.Name}
                                                                    onClick={() => handleAttributeChange(row.id, opt.Name, true)}
                                                                >
                                                                    {opt.Name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </TableCell>

                                            <TableCell className="border p-2 text-center">=</TableCell>
                                            <TableCell className="border p-2 text-sm font-medium">{row.FunctionName}</TableCell>

                                            <TableCell className="border p-2">
                                                <div className="flex flex-col gap-2">
                                                    {row.parameters.map((param, idx) => (
                                                        <div key={idx} className="flex gap-2 items-center">
                                                            <span className="text-xs text-gray-500 w-16">Param {idx + 1}:</span>
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="outline" className="text-sm flex-1">
                                                                        {dataType.length > 0 ? !param.dataType ? "Select" : param.dataType : "Fetching DataType..."}
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent>
                                                                    {dataType.map((dt) => (
                                                                        <DropdownMenuItem
                                                                            className="w-[280px]"
                                                                            key={dt.ID}
                                                                            onClick={() => handleDataTypeChange(row.id, dt.ID?.toString() || "", dt.Name, false, idx)}
                                                                        >
                                                                            {dt.Name}
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>

                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button variant="outline" className="text-sm flex-1" disabled={!param.dataType || attribute.length === 0}>
                                                                        {param.dataType ? !param.attribute ? "Select" : param.attribute : "Select"}
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent>
                                                                    {attribute.map((opt) => (
                                                                        <DropdownMenuItem
                                                                            className="w-[250px]"
                                                                            key={opt.Name}
                                                                            onClick={() => handleAttributeChange(row.id, opt.Name, false, idx)}
                                                                        >
                                                                            {opt.Name}
                                                                        </DropdownMenuItem>
                                                                    ))}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </div>
                                                    ))}
                                                </div>
                                            </TableCell>

                                            <TableCell className="border p-2 text-sm">{row.Description}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {addWarning && (
                                <div
                                    className="text-center text-red-600 mt-2 font-medium opacity-100 transition-opacity duration-700"
                                    style={{ opacity: addWarning ? 1 : 0 }}
                                >
                                    {addWarning}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-center mt-4">
                            <Button onClick={handleSaveExpression}>
                                Save Expression
                            </Button>
                        </div>
                    </div>
                )}

                <Textarea
                    value={ruleExpression}
                    placeholder="Final rule expression will appear here"
                    className="w-full md:w-full sm:w-3/4 bg-gray-100 text-gray-700 border border-black rounded-md"
                    rows={4}
                    disabled
                />
            </div>
        </div>
    );
}